import { TFunction } from "i18next";
import { Link } from "react-router-dom";

import { EventTypeCellParts, EventTypeStrategy } from "./EventTypeStrategy";
import { getTranslatedEventTypeName } from "../../utils/eventTypeUtils";

import { Button } from "@/components/ui/v2/button";
import { ROUTES } from "@/constants/routes";
import { TransactionType } from "@/types/transaction/TransactionType";
import { formatCurrency } from "@/utils/valueFormat";

export class ChannelInvoiceUpdatedEventTypeStrategy
implements EventTypeStrategy
{
  renderCell(row: TransactionType, t: TFunction): JSX.Element {
    const forText = row.eventData.channelName ? (
      <>
        {" "}
        {t("for")} <b>{row.eventData.channelName}</b>
      </>
    ) : (
      ""
    );
    const inText = row.eventData.month ? (
      <>
        {" "}
        {t("in")}{" "}
        <b>
          {new Date(row.eventData.month).toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </b>
      </>
    ) : (
      ""
    );
    const previousAmountText = row.eventData.previousAmount ? (
      <>
        {" | "}
        {t("common.previous")} {t("common.amount")}:{" "}
        <b>{formatCurrency(row.eventData.previousAmount)}</b>
      </>
    ) : (
      ""
    );
    const allocationReleasedText = row.eventData.allocatedAmount ? (
      <>
        {" | "}
        {t("common.allocation")} {t("common.released")}:{" "}
        <b>{formatCurrency(row.eventData.allocatedAmount)}</b>
      </>
    ) : (
      ""
    );

    return (
      <>
        {t(`eventTypes.${row.eventType}`)}
        {forText}
        {inText}
        {previousAmountText}
        {allocationReleasedText}
      </>
    );
  }

  getTypeAndDescription(
    row: TransactionType,
    t: TFunction
  ): EventTypeCellParts {
    const channelName = row.eventData.channelName || "";
    const channelId = row.eventData.channelId;
    const month = row.eventData.month;
    const previousAmount = row.eventData.previousAmount;

    const parts: (string | JSX.Element)[] = [];

    // "for [Channel Name (link)]"
    if (channelName && channelId && month) {
      if (parts.length > 0) parts.push(" ");
      const monthDate = new Date(month);
      const year = monthDate.getFullYear();
      const monthNum = monthDate.getMonth() + 1;
      const channelLink = `/${ROUTES.invoices}/${ROUTES.channels}/${channelId}/${ROUTES.invoiced}/${year}/${monthNum}`;

      parts.push(
        <>
          {t("common.for")}{" "}
          <Button asChild variant={"link"} className="px-0">
            <Link to={channelLink}>{channelName}</Link>
          </Button>
        </>
      );
    } else if (channelName) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.for")} <b>{channelName}</b>
        </>
      );
    } else if (channelId) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.for")} <b>{channelId}</b>
        </>
      );
    }

    // "in [Month Year]"
    if (month) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.in")}{" "}
          <b>
            {new Date(month).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </b>
        </>
      );
    }

    // Show previous amount
    if (previousAmount !== null && previousAmount !== undefined) {
      if (parts.length > 0) parts.push(" | ");
      parts.push(
        <>
          {t("common.previous")} {t("common.amount")}:{" "}
          <b>{formatCurrency(previousAmount)}</b>
        </>
      );
    }

    return {
      type: <>{getTranslatedEventTypeName(row.eventType, t)}</>,
      description: <>{parts}</>,
    };
  }
}
